// The desktop's window registry as a pure state machine (same philosophy
// as engine/gameMachine.js): ONE source of truth, everything else
// derived. A window exists iff it's in the stack — so the taskbar,
// minimize state, focus, and z-order cannot disagree with each other.
//
// State shape:
//   {
//     stack: [ { id, kind, minimized, modal, priority, openSeq, payload } ],
//     popup: { id, ... } | null,
//     nextSeq,
//   }
// stack is back-to-front: ARRAY ORDER IS THE Z-ORDER. openSeq is a
// monotonic counter recording when each window was first opened — the
// taskbar lists windows in open order (real Win95), not stack order.
//
// Modality: at most ONE modal window, ever. Opening a modal while one is
// up either REPLACES it (strictly higher priority — e.g. "timed out"
// over "still there?") or is REFUSED (equal/lower). Refusals return the
// state object unchanged (===), so callers can detect and log them. A
// modal is always top of stack; while it's up, focus/minimize/taskbar
// ops on other windows are refused (the caller answers those with the
// Win95 ding + title-bar flash).
//
// Pure module: no React, no DOM. Side-effectful concerns (minimize
// zoom rectangles, sounds) live with the caller.

export const KIND = Object.freeze({
  GAME: "game",
  TOOL: "tool",
  DIALOG: "dialog",
});

// Dialog priorities (higher replaces lower): timed-out > user confirms >
// net trouble > still-there — when the connection is down, "still
// there?" is a question whose answer we can't deliver, so net-trouble
// outranks it.
export const PRIORITY = Object.freeze({
  TIMED_OUT: 100,
  CONFIRM: 50,
  NET_TROUBLE: 30,
  STILL_THERE: 20,
});

export const initialState = Object.freeze({
  stack: [],
  popup: null,
  nextSeq: 1,
});

// ---------------------------------------------------------------------
// Selectors (all derived, never stored)
// ---------------------------------------------------------------------

export function getWindow(state, id) {
  return state.stack.find((w) => w.id === id) ?? null;
}

export function isOpen(state, id) {
  return getWindow(state, id) != null;
}

export function isMinimized(state, id) {
  const w = getWindow(state, id);
  return w ? w.minimized : false;
}

// At most one modal exists; it is always top of stack.
export function activeModal(state) {
  const top = state.stack[state.stack.length - 1];
  return top && top.modal ? top : null;
}

// The focused window: the modal if one is up, else the topmost
// non-minimized window. Derived — it can never name a closed window.
export function focusedId(state) {
  const m = activeModal(state);
  if (m) return m.id;
  for (let i = state.stack.length - 1; i >= 0; i--) {
    if (!state.stack[i].minimized) return state.stack[i].id;
  }
  return null;
}

// z-index for CSS: stack position, offset above the desktop icons.
export function zIndexOf(state, id) {
  const i = state.stack.findIndex((w) => w.id === id);
  return i < 0 ? 1 : i + 2;
}

// Taskbar entries: every non-dialog window that wants one, in OPEN
// order (Win95 taskbar order is the order things were opened, not the
// z-order) — except pinned windows, which always sort first. YOUR game
// is pinned: it must lead the taskbar even after a close/re-open cycle
// (timed out → deal new would otherwise send it to the end, because a
// re-opened window gets a fresh openSeq).
export function taskbarWindows(state) {
  return state.stack
    .filter((w) => w.kind !== KIND.DIALOG && w.taskbar !== false)
    .slice()
    .sort((a, b) => {
      if (!!a.pin !== !!b.pin) return a.pin ? -1 : 1;
      return a.openSeq - b.openSeq;
    });
}

// ---------------------------------------------------------------------
// Transitions
// ---------------------------------------------------------------------

function raise(stack, id) {
  const i = stack.findIndex((w) => w.id === id);
  if (i < 0 || i === stack.length - 1) return stack;
  const w = stack[i];
  return [...stack.slice(0, i), ...stack.slice(i + 1), w];
}

export function reducer(state, action) {
  switch (action.type) {
    // open {id, kind, modal?, priority?, payload?}
    // Existing window: raise + restore (+ refresh payload). New window:
    // push on top — except a new NON-modal under an active modal, which
    // slots in just beneath it (the modal must stay top).
    case "open": {
      const {
        id,
        kind,
        modal = false,
        priority = 0,
        payload,
        // Some windows never get a taskbar button (Display Properties —
        // control-panel dialogs didn't have one in Win95 either).
        taskbar = true,
        // Passive arrivals (neighbor windows dealt in by the server)
        // open at the BOTTOM of the stack — they must never cover the
        // window you're using.
        behind = false,
        // Pinned windows lead the taskbar regardless of open order.
        pin = false,
      } = action;
      const existing = getWindow(state, id);
      const curModal = activeModal(state);

      if (existing) {
        if (curModal && curModal.id !== id) return state; // refused
        // Idempotence guard: re-opening the already-topmost window with
        // the same payload is a no-op (===), so declarative "keep this
        // open" effects can dispatch freely without looping.
        if (
          !existing.minimized &&
          state.stack[state.stack.length - 1] === existing &&
          (payload === undefined || payload === existing.payload)
        ) {
          return state;
        }
        const stack = raise(state.stack, id).map((w) =>
          w.id === id
            ? {
                ...w,
                minimized: false,
                payload: payload ?? w.payload,
                // Same-id re-opens may re-rank (the system-notice slot
                // swaps content and priority together).
                priority: action.priority ?? w.priority,
              }
            : w,
        );
        return { ...state, stack };
      }

      const win = {
        id,
        kind,
        modal,
        priority,
        minimized: false,
        openSeq: state.nextSeq,
        payload,
        taskbar,
        pin,
      };

      if (behind && !modal) {
        return {
          ...state,
          stack: [win, ...state.stack],
          nextSeq: state.nextSeq + 1,
        };
      }

      if (curModal) {
        if (modal) {
          if (priority > curModal.priority) {
            // Replace: the newcomer's premise supersedes (timed-out over
            // still-there). The replaced dialog does NOT come back — its
            // premise is stale by definition.
            const stack = [
              ...state.stack.filter((w) => w.id !== curModal.id),
              win,
            ];
            return { ...state, stack, popup: null, nextSeq: state.nextSeq + 1 };
          }
          return state; // refused: one modal at a time
        }
        // Non-modal opens go UNDER the modal.
        const stack = [...state.stack.slice(0, -1), win, curModal];
        return { ...state, stack, nextSeq: state.nextSeq + 1 };
      }

      return {
        ...state,
        stack: [...state.stack, win],
        // Opening a window dismisses any transient popup (menus close
        // when the world changes under them).
        popup: modal ? null : state.popup,
        nextSeq: state.nextSeq + 1,
      };
    }

    // close {id}: remove from existence. The ONLY way a window leaves,
    // and it leaves everywhere at once (taskbar, focus, z-order).
    case "close": {
      if (!isOpen(state, action.id)) return state;
      return {
        ...state,
        stack: state.stack.filter((w) => w.id !== action.id),
      };
    }

    // focus {id}: raise + restore. Refused for non-modal targets while a
    // modal is up (you can't focus past a modal).
    case "focus": {
      const w = getWindow(state, action.id);
      if (!w) return state;
      const m = activeModal(state);
      if (m && m.id !== action.id) return state; // refused
      if (!w.minimized && state.stack[state.stack.length - 1] === w) {
        return state; // already focused
      }
      const stack = raise(state.stack, action.id).map((x) =>
        x.id === action.id ? { ...x, minimized: false } : x,
      );
      return { ...state, stack };
    }

    // minimize {id}: keep stack position, hide. Modals can't minimize.
    case "minimize": {
      const w = getWindow(state, action.id);
      if (!w || w.modal || w.minimized) return state;
      if (activeModal(state)) return state; // refused under a modal
      return {
        ...state,
        stack: state.stack.map((x) =>
          x.id === action.id ? { ...x, minimized: true } : x,
        ),
      };
    }

    // taskbarClick {id}: the Win95 button rule — restore if minimized,
    // minimize if it's the active window, raise otherwise.
    case "taskbarClick": {
      const w = getWindow(state, action.id);
      if (!w) return state;
      if (activeModal(state)) return state; // refused: modal owns input
      if (w.minimized) return reducer(state, { type: "focus", id: action.id });
      if (focusedId(state) === action.id) {
        return reducer(state, { type: "minimize", id: action.id });
      }
      return reducer(state, { type: "focus", id: action.id });
    }

    // Transient popups (Start menu, context menus): one slot, at most
    // one open; opening another replaces it. Refused while a modal is up.
    case "openPopup": {
      if (activeModal(state)) return state;
      return { ...state, popup: action.popup };
    }
    case "closePopup": {
      if (state.popup == null) return state;
      return { ...state, popup: null };
    }

    default:
      return state;
  }
}
